import type { FerramentaOficinaCorte } from './oficinaCorte'

/**
 * Terceira porta: abertura de emergência cortada onde um veículo de duas
 * portas não tem porta traseira. A oficina trata isso como um modo do
 * exercício sobre o footprint da porta traseira existente (mesmas
 * coordenadas de `perfil.portaTraseira`), em vez de modelar uma carroceria
 * de duas portas inteira — o professor pode ajustar essa escolha depois.
 *
 * Sem dobradiça, trava ou limitador para liberar: a chapa é maciça e o
 * acesso nasce do corte. Seguindo o mesmo padrão de "lata de sardinha"
 * (teto) e da tampa traseira, corta-se três lados — superior, inferior e
 * traseiro — e a aba dobra para fora usando a borda dianteira como
 * dobradiça, a mesma articulação da porta comum.
 *
 * Ferramenta prevista no plano para este corte é a serra-sabre; como ainda
 * não há modelo 3D para ela, esta primeira versão usa o cortador
 * hidráulico (já articulado) e registra a lacuna para quando o modelo da
 * serra-sabre existir.
 */

export type IdLadoCorteTerceiraPorta = 'superior' | 'inferior' | 'traseiro'
export type EstadoLadoCorte = 'integro' | 'cortado'

export interface EstadoTerceiraPortaOficina {
  lados: Record<IdLadoCorteTerceiraPorta, EstadoLadoCorte>
  abertura: number
  sustentada: boolean
  removida: boolean
}

export type OperacaoTerceiraPorta =
  | 'cortarLadoSuperior'
  | 'cortarLadoInferior'
  | 'cortarLadoTraseiro'
  | 'sustentarAba'
  | 'removerAba'

export type IdZonaOperacaoTerceiraPorta = IdLadoCorteTerceiraPorta

export const ZONAS_POR_OPERACAO_TERCEIRA_PORTA: Readonly<Record<OperacaoTerceiraPorta, readonly IdZonaOperacaoTerceiraPorta[]>> = {
  cortarLadoSuperior: ['superior'],
  cortarLadoInferior: ['inferior'],
  cortarLadoTraseiro: ['traseiro'],
  sustentarAba: [],
  removerAba: [],
}

export const OPERACOES_TERCEIRA_PORTA: readonly {
  id: OperacaoTerceiraPorta
  nome: string
  instrucao: string
  usaFerramenta: boolean
}[] = [
  { id: 'cortarLadoSuperior', nome: 'Cortar lado superior', instrucao: 'Corte a chapa junto à linha de cintura.', usaFerramenta: true },
  { id: 'cortarLadoInferior', nome: 'Cortar lado inferior', instrucao: 'Corte a chapa junto à soleira.', usaFerramenta: true },
  { id: 'cortarLadoTraseiro', nome: 'Cortar lado traseiro', instrucao: 'Corte a chapa junto à coluna traseira, mantendo a borda dianteira como dobradiça.', usaFerramenta: true },
  { id: 'sustentarAba', nome: 'Sustentar a aba', instrucao: 'Prepare o recebimento da aba antes de dobrá-la totalmente para fora.', usaFerramenta: false },
  { id: 'removerAba', nome: 'Remover a aba', instrucao: 'Conclua somente após cortar os três lados.', usaFerramenta: false },
]

export interface ComandoOperacaoTerceiraPorta {
  operacao: OperacaoTerceiraPorta
  ferramenta?: FerramentaOficinaCorte
  zona?: IdZonaOperacaoTerceiraPorta | null
  orientacaoValida?: boolean
}

export type CodigoResultadoOperacaoTerceiraPorta =
  | 'concluida'
  | 'semProgresso'
  | 'ferramentaIncompativel'
  | 'ladosRestantes'
  | 'abaSemApoio'
  | 'abaJaRemovida'
  | 'posicaoAusente'
  | 'zonaIncompativel'
  | 'orientacaoIncompativel'

export interface ResultadoOperacaoTerceiraPorta {
  codigo: CodigoResultadoOperacaoTerceiraPorta
  concluiu: boolean
  estado: EstadoTerceiraPortaOficina
  mensagem: string
  alteracoes: readonly string[]
}

const LADOS: readonly IdLadoCorteTerceiraPorta[] = ['superior', 'inferior', 'traseiro']

const FERRAMENTAS_CORTE: readonly FerramentaOficinaCorte[] = ['cortadorHidraulico']

export function estadoInicialTerceiraPortaOficina(): EstadoTerceiraPortaOficina {
  return {
    lados: { superior: 'integro', inferior: 'integro', traseiro: 'integro' },
    abertura: 0,
    sustentada: false,
    removida: false,
  }
}

export function ladosRestantesTerceiraPorta(estado: EstadoTerceiraPortaOficina): IdLadoCorteTerceiraPorta[] {
  return LADOS.filter((id) => estado.lados[id] !== 'cortado')
}

export function abaProntaParaRemocao(estado: EstadoTerceiraPortaOficina): boolean {
  return ladosRestantesTerceiraPorta(estado).length === 0 && estado.sustentada && !estado.removida
}

function copiarEstado(estado: EstadoTerceiraPortaOficina): EstadoTerceiraPortaOficina {
  return { ...estado, lados: { ...estado.lados } }
}

function resultado(
  estado: EstadoTerceiraPortaOficina,
  codigo: CodigoResultadoOperacaoTerceiraPorta,
  mensagem: string,
  alteracoes: readonly string[] = [],
): ResultadoOperacaoTerceiraPorta {
  return { codigo, concluiu: codigo === 'concluida', estado, mensagem, alteracoes }
}

function ferramentaAceita(
  ferramenta: FerramentaOficinaCorte | undefined,
  aceitas: readonly FerramentaOficinaCorte[],
): boolean {
  return ferramenta !== undefined && aceitas.includes(ferramenta)
}

/**
 * Executa uma única ação sobre a aba da terceira porta. Mesmas garantias das
 * demais peças da oficina: resultado parcial preservado e motivo explícito
 * quando a tentativa não avança.
 */
export function executarOperacaoTerceiraPorta(
  atual: EstadoTerceiraPortaOficina,
  comando: ComandoOperacaoTerceiraPorta,
): ResultadoOperacaoTerceiraPorta {
  const estado = copiarEstado(atual)
  if (estado.removida) {
    return resultado(estado, 'abaJaRemovida', 'A aba já foi removida neste exercício.')
  }

  if (comando.operacao === 'sustentarAba') {
    estado.sustentada = true
    return resultado(estado, 'concluida', 'A aba está sustentada para a liberação final.', ['sustentada'])
  }

  if (comando.operacao === 'removerAba') {
    const restantes = ladosRestantesTerceiraPorta(estado)
    if (restantes.length > 0) {
      return resultado(estado, 'ladosRestantes', `Ainda há lados por cortar: ${restantes.join(', ')}.`)
    }
    if (!estado.sustentada) {
      return resultado(estado, 'abaSemApoio', 'Sustente a aba antes de concluir a remoção.')
    }
    estado.removida = true
    estado.abertura = 1
    return resultado(estado, 'concluida', 'Aba removida; abertura da terceira porta liberada.', ['removida', 'abertura'])
  }

  const zonasAceitas = ZONAS_POR_OPERACAO_TERCEIRA_PORTA[comando.operacao]
  if (zonasAceitas.length > 0 && !comando.zona) {
    return resultado(estado, 'posicaoAusente', 'Selecione um ponto de aplicação antes de acionar a ferramenta.')
  }
  if (comando.zona && !zonasAceitas.includes(comando.zona)) {
    return resultado(estado, 'zonaIncompativel', 'O ponto selecionado não corresponde ao lado escolhido.')
  }
  if (zonasAceitas.length > 0 && comando.orientacaoValida === false) {
    return resultado(estado, 'orientacaoIncompativel', 'A ferramenta está em contato, mas precisa ser orientada corretamente antes do acionamento.')
  }
  if (!ferramentaAceita(comando.ferramenta, FERRAMENTAS_CORTE)) {
    return resultado(estado, 'ferramentaIncompativel', 'O corte deste lado exige o cortador hidráulico.')
  }

  const cortar = (id: IdLadoCorteTerceiraPorta, mensagem: string): ResultadoOperacaoTerceiraPorta => {
    estado.lados[id] = 'cortado'
    const restantes = ladosRestantesTerceiraPorta(estado)
    if (restantes.length === 0) estado.abertura = Math.max(estado.abertura, 0.4)
    return resultado(estado, 'concluida', mensagem, [`lados.${id}`, 'abertura'])
  }

  switch (comando.operacao) {
    case 'cortarLadoSuperior':
      return cortar('superior', 'O lado superior foi cortado.')
    case 'cortarLadoInferior':
      return cortar('inferior', 'O lado inferior foi cortado.')
    case 'cortarLadoTraseiro':
      return cortar('traseiro', 'O lado traseiro foi cortado; a borda dianteira permanece como dobradiça.')
    default:
      return resultado(estado, 'semProgresso', 'A operação não alterou o estado da aba.')
  }
}
